import api from "./api.service";
import { adminTokenService } from "./admin.token.service";

export interface AdminCustomer {
    id: number;
    code?: string;
    firstName: string;
    lastName: string;
    email: string;
    dni: string;
    kindOfDNI: string;
    phoneNumber: string;
    isActive: boolean;
    isVerify: boolean;
    isO2Country: string;
    createdAt: string;
    orderCount?: number;
}

export interface GetAdminCustomersParams {
    page?: number;
    pageSize?: number;
    isActive?: boolean;
    SearchString?: string;
}

export interface GetAdminCustomersResponse {
    statusCode: number;
    message: string;
    data: AdminCustomer[];
    pagination: {
        totalCount: number;
        page: number;
        pageSize: number;
        totalPages: number;
    };
}

export interface UpdateCustomerActiveData {
    IdCustomer: number;
    IsActive: boolean;
}

class AdminCustomerService {
    /**
     * Fetches a list of customers for administration purposes.
     * @param params Filtering and pagination parameters.
     * @returns A promise with the customers data and pagination info.
     */
    async getCustomers(params?: GetAdminCustomersParams): Promise<GetAdminCustomersResponse> {
        const token = adminTokenService.getAdminToken();
        try {
            const response = await api.get<GetAdminCustomersResponse>(
                "/admin/get-customers",
                {
                    params,
                    headers: {
                        Authorization: `${token}`,
                    },
                }
            );
            return response.data;
        } catch (error) {
            console.error("Error fetching admin customers:", error);
            throw error;
        }
    }

    /**
     * Updates the active status of a customer.
     * @param data The customer ID and new active status.
     * @returns A promise with the updated customer data.
     */
    async updateCustomerActive(data: UpdateCustomerActiveData): Promise<any> {
        const token = adminTokenService.getAdminToken();
        try {
            const response = await api.post(
                "/admin/update-customer-active",
                data,
                {
                    headers: {
                        Authorization: `${token}`,
                    },
                }
            );
            return response.data;
        } catch (error) {
            console.error("Error updating customer active status:", error);
            throw error;
        }
    }

    /**
     * Updates customer general data.
     */
    async updateCustomerData(data: AdminUpdateCustomerData): Promise<any> {
        const token = adminTokenService.getAdminToken();
        try {
            const response = await api.post(
                "/admin/update-customer-data",
                data,
                {
                    headers: {
                        Authorization: `${token}`,
                    },
                }
            );
            return response.data;
        } catch (error) {
            console.error("Error updating customer general data:", error);
            throw error;
        }
    }

    /**
     * Soft deletes a customer by changing their status.
     * @param idCustomer Customer ID to delete.
     */
    async deleteCustomer(idCustomer: number): Promise<any> {
        const token = adminTokenService.getAdminToken();
        try {
            const response = await api.post(
                "/admin/delete-customer",
                { idCustomer },
                {
                    headers: {
                        Authorization: `${token}`,
                    },
                }
            );
            return response.data;
        } catch (error) {
            console.error("Error deleting customer:", error);
            throw error;
        }
    }
}

export interface AdminUpdateCustomerData {
    idCustomer: number;
    firstName: string;
    lastName: string;
    email: string;
    password?: string;
    kindOfDNI?: string;
    DNI?: string;
    phoneNumber?: string;
    isActive: boolean;
    isVerify: boolean;
    iso2Country?: string;
}

export const adminCustomerService = new AdminCustomerService();

